import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Menu } from 'lucide-react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginPage from './components/LoginPage';
import AdminDashboard from './components/AdminDashboard';
import Sidebar from './components/Sidebar';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import Dashboard from './pages/Dashboard';
import PupilsPage from './pages/PupilsPage';
import QuestionsPage from './pages/QuestionsPage';
import PupilLogsPage from './pages/PupilLogsPage';
import ReflectionLogsPage from './pages/ReflectionLogsPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import './App.css';

// Header Component
const Header = () => {
  const { toggleMobileSidebar } = useSidebar();
  
  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 flex items-center justify-between px-6 lg:px-8">
      <div className="flex items-center space-x-4">
        <Button variant="outline" size="sm" onClick={toggleMobileSidebar} className="lg:hidden">
          <Menu size={20} />
        </Button>
        <div className="lg:hidden">
          <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-200">ReflectLogs</h1>
        </div>
        <div className="hidden lg:block">
          <div className="text-sm text-gray-500 dark:text-green-600">
            Professional driving lesson management
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4"></div>
    </header>
  );
};

// Main Layout Component with Sidebar Context
const MainLayout = () => {
  const { isCollapsed } = useSidebar();
  
  return (
    <div className="App min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header />
        <main className="p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pupils" element={<PupilsPage />} />
            <Route path="/pupil/:pupilId/logs" element={<PupilLogsPage />} />
            <Route path="/questions" element={<QuestionsPage />} />
            <Route path="/logs" element={<ReflectionLogsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Protected Route Wrapper
const ProtectedRoutes = () => {
  return (
    <SidebarProvider>
      <MainLayout />
    </SidebarProvider>
  );
};

// Main App Component
const AppContent = () => {
  const { isAuthenticated, isAdmin, isVerified, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading ReflectLogs...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (isAdmin) {
    return <AdminDashboard />;
  }

  if (!isVerified) {
    return <VerifyEmailPage />;
  }

  return <ProtectedRoutes />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
          <Toaster />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
